import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHtmlSafeCore, kHtmlSafePolicy } from "../../../Admin/kReusableCoreTS/kHtmlSafeCore";

// AppAssistRich - the rules for TABLES AND CHARTS in an AI answer (a ```wac-html fence the model
// writes: an HTML `<table>` or an inline `<svg>` chart). Read by BOTH halves: the app's panel
// (`AppAssistRules.Blocks` finds the fence, `AppAssistPanel` builds it) and the service
// (`AssistRules`, which tells the model the fence and keeps earlier turns small with `Summary`).
//
// ⛔ WHY THIS IS DANGEROUS, SAID ONCE: the panel runs beside the `browser` archive key, which can SEND
// WhatsApp messages as the owner, and a contact's own messages can steer what the model writes. So a
// fence is NEVER markup handed to the browser: it is parsed by the shared pure parser, cut down by the
// shared allow-list (`kHtmlSafeCore.Clean` with `Policy()`), and every survivor is BUILT element by
// element with text as text (`AppAssistPanel`). Nothing here widens that allow-list; it only names
// the classes a chart may colour with.
//
// ⚠ Pure: no DOM, no `window`, no transport - the Bridge suite imports it like `AppAssistApi`.

export class AppAssistRich {
    /** The fence's info string: a line that is exactly "```wac-html" opens it, a line "```" closes it. */
    public static readonly Fence: string = "wac-html";
    /** The only classes a table cell or chart shape may carry - the palette `.c1`..`.c6` and three roles. */
    public static readonly Classes: string[] = ["c1", "c2", "c3", "c4", "c5", "c6", "muted", "num", "total"];
    /** Every rich block of ONE answer together; past it, the rest are the too-large note. */
    public static readonly RichMaxChars: number = 24000;
    /** Shown in place of a block that was over a limit. ⚠ Shown content in muted ink, never an error. */
    public static readonly TooLargeNote: string = "(A chart or table was too large to show.)";
    /** Shown in place of a block of which nothing safe was left. */
    public static readonly DroppedNote: string = "(A chart or table could not be shown.)";

    /** Every id a fence keeps starts with this, then the block's `Scope`. */
    public static readonly IdPrefix: string = "wac-ai-";

    /**
     * The allow-list a fence is cleaned with: the shared AI-answer policy, with our classes.
     * ⚠ `Scope` NAMES THE BLOCK ("3-1-": answer 3, its first fence). The cleaner prefixes every id and
     * every `url(#id)`; the whole log is one document, so without a per-block prefix two charts that
     * both say `id="g1"` would both paint with whichever gradient came first.
     */
    public static Policy = (Scope: string = ""): kHtmlSafePolicy => {
        if (getCoreUtils().KErrorMode === true) { return new kHtmlSafePolicy(); }
        try {
            const Out: kHtmlSafePolicy = kHtmlSafeCore.AiAnswerPolicy(AppAssistRich.Classes.slice());
            Out.IdPrefix = AppAssistRich.IdPrefix + nz(Scope).replace(/[^A-Za-z0-9-]/g, "");
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.Policy");
            return new kHtmlSafePolicy();
        }
    };

    /**
     * ⭐ THE CHART SPEC FENCE (2026-09-29, `AppAssistChart`): a line "```wac-chart" opens a JSON spec the SERVICE
     * draws. ⛔ Only the service's readers of the MODEL'S raw text accept it (`Any` true: `AssistRich`); the page
     * and the history only ever hold ```wac-html, so their readers keep `Any` false.
     */
    public static readonly ChartFence: string = "wac-chart";

    /**
     * Whether a line opens a fence: "```wac-html" - or, with `Any`, "```wac-chart" too - surrounding spaces and
     * letter case ignored.
     */
    public static IsOpen = (Line: string, Any: boolean = false): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Low: string = nz(Line).trim().toLowerCase();
            if (Low === "```" + AppAssistRich.Fence) { return true; }
            return Any === true && Low === "```" + AppAssistRich.ChartFence;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.IsOpen");
            return false;
        }
    };

    /**
     * The line that closes the fence opened at `At`, or -1 when `At` opens none or it never closes.
     * ⛔ AN UNCLOSED FENCE IS TEXT - a cut-off answer must not turn the rest of itself into a chart.
     */
    public static CloseAt = (Lines: string[], At: number, Any: boolean = false): number => {
        if (getCoreUtils().KErrorMode === true) { return -1; }
        try {
            if (At < 0 || At >= Lines.length || AppAssistRich.IsOpen(Lines[At], Any) !== true) { return -1; }
            for (let End: number = At + 1; End < Lines.length; End = End + 1) {
                if (nz(Lines[End]).trim() === "```") { return End; }
            }
            return -1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.CloseAt");
            return -1;
        }
    };

    /**
     * The answer with each closed fence replaced by "[table]" (it holds a `<table`) or "[chart]" - what
     * an earlier turn carries back to the model, so a follow-up does not resend kilobytes of markup.
     */
    public static Summary = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Lines: string[] = nz(Text).replace(/\r\n?/g, "\n").split("\n");
            const Out: string[] = [];
            let At: number = 0;
            while (At < Lines.length) {
                const End: number = AppAssistRich.CloseAt(Lines, At);
                if (End < 0) { Out.push(Lines[At]); At = At + 1; continue; }
                Out.push(AppAssistRich.KindOf(Lines.slice(At + 1, End).join("\n")));
                At = End + 1;
            }
            return Out.join("\n");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.Summary");
            return "";
        }
    };

    /** "[table]" for a source holding a `<table`, else "[chart]". */
    public static KindOf = (Source: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "[chart]"; }
        try {
            if (/<table[\s>/]/i.test(nz(Source) + " ") === true) { return "[table]"; }
            return "[chart]";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.KindOf");
            return "[chart]";
        }
    };

    /** Whether a line opens a chart SPEC fence ("```wac-chart", spaces and case ignored). */
    public static IsChartOpen = (Line: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return nz(Line).trim().toLowerCase() === "```" + AppAssistRich.ChartFence;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.IsChartOpen");
            return false;
        }
    };

    /** Whether a block of `Size` characters still fits once `Spent` of the answer's budget is used. */
    public static Fits = (Spent: number, Size: number): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Spent + Size <= AppAssistRich.RichMaxChars;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.Fits");
            return false;
        }
    };

    /**
     * The note a cleaned block shows instead of itself: "" when something survived to be drawn, the
     * too-large note for a limit the shared cleaner hit, else the could-not-be-shown note.
     */
    public static NoteFor = (Refused: string, Survived: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return AppAssistRich.DroppedNote; }
        try {
            const Why: string = nz(Refused);
            if (Why === "too-large" || Why === "too-many-nodes" || Why === "too-deep") { return AppAssistRich.TooLargeNote; }
            if (Why.length > 0 || Survived !== true) { return AppAssistRich.DroppedNote; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistRich.NoteFor");
            return AppAssistRich.DroppedNote;
        }
    };
}
